import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { sentry } from '@frontend/tracking';
import {
  importJustifiPaymentProvisioning,
  importJustifiTokenizePaymentMethod,
} from './use-justifi-loader-store/import-methods';

export type JustifiLoadableComponents = 'justifi-payment-provisioning' | 'justifi-tokenize-payment-method';

export type JustifiModules = 'onboarding' | 'paymentMethod';

export interface ComponentState {
  isLoaded: boolean;
  isSuccess: boolean;
  isLoadingSlow: boolean;
}

export interface ModuleStatus extends ComponentState {
  hasErrorLoading: boolean;
}

type LoaderStoreComponentsKeyed = {
  [key in JustifiLoadableComponents]: ComponentState;
};

export interface JustifiLoaderStore {
  isModuleLoaded: (moduleName: JustifiModules) => boolean;
  getModuleStatus: (moduleName: JustifiModules) => ModuleStatus;
  loadPaymentMethodModule: (force?: boolean) => Promise<void>;
  loadOnboardingModule: (force?: boolean) => Promise<void>;
  loadModules: (modules: JustifiModules[], force?: boolean) => Promise<void>;
  resetStore: () => void;
  components: LoaderStoreComponentsKeyed;
}

const COMPONENT_MODULE_MAP: Record<JustifiModules, JustifiLoadableComponents> = {
  onboarding: 'justifi-payment-provisioning',
  paymentMethod: 'justifi-tokenize-payment-method',
};

// Move the timeout refs outside the store to make them persistent
const timeoutHandles = new Map<JustifiModules, NodeJS.Timeout>();
const attemptCounts = new Map<JustifiModules, number>();
const SLOW_LOADING_TIMEOUT = 2000; // 2 seconds
const MAX_RETRY_ATTEMPTS = 3;

export const useJustifiLoaderStore = createStoreWithSubscribe<JustifiLoaderStore>(
  (set, get) => {
    const getComponentNameByModule = (moduleName: JustifiModules): JustifiLoadableComponents => {
      const componentName = COMPONENT_MODULE_MAP[moduleName];
      if (!componentName) {
        throw new Error(`Component name for module ${moduleName} is not defined.`);
      }
      return componentName;
    };

    const clearModuleTimeout = (moduleName: JustifiModules) => {
      const timeoutId = timeoutHandles.get(moduleName);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutHandles.delete(moduleName);
      }
    };

    const startSlowLoadingTimeout = (moduleName: JustifiModules) => {
      // Clear any existing timeout for this moduleName
      clearModuleTimeout(moduleName);

      const timeoutId = setTimeout(() => {
        const componentName = getComponentNameByModule(moduleName);
        sentry.warn({
          error: 'justifi module -> slow loading timeout',
          topic: 'loader-store-slow-loading',
          team: 'payments',
          addContext: {
            name: 'JustifiLoaderStore',
            context: {
              moduleName,
            },
          },
        });
        set((state) => {
          state.components[componentName].isLoadingSlow = true;
        });
        timeoutHandles.delete(moduleName);
      }, SLOW_LOADING_TIMEOUT);

      timeoutHandles.set(moduleName, timeoutId);
    };

    const getModuleStatus = (moduleName: JustifiModules): ModuleStatus => {
      const component = getComponentNameByModule(moduleName);
      const componentState = get().components[component] ?? { isLoaded: false, isSuccess: false, isLoadingSlow: false };
      return {
        ...componentState,
        hasErrorLoading: componentState.isLoaded && !componentState.isSuccess,
      };
    };

    const isModuleLoaded = (moduleName: JustifiModules) => {
      return getModuleStatus(moduleName).isLoaded;
    };
    const loadJustifiModule = async (moduleName: JustifiModules, importCallback: () => Promise<any>, force = false) => {
      if (isModuleLoaded(moduleName) && !force) {
        return;
      }

      const componentName = getComponentNameByModule(moduleName);
      let currentAttempt = attemptCounts.get(moduleName) || 0;

      // Start the slow loading timeout
      startSlowLoadingTimeout(moduleName);

      const attemptLoad = async (): Promise<void> => {
        currentAttempt++;
        attemptCounts.set(moduleName, currentAttempt);

        try {
          await importCallback();

          // Clear timeout and attempts on successful load
          clearModuleTimeout(moduleName);
          attemptCounts.delete(moduleName);
          set((state) => {
            state.components[componentName] = { isLoaded: true, isSuccess: true, isLoadingSlow: false };
          });
        } catch (error) {
          console.warn(`Module ${moduleName} failed to load on attempt ${currentAttempt}`, error);

          if (currentAttempt < MAX_RETRY_ATTEMPTS) {
            // Retry after a short delay
            await new Promise((resolve) => setTimeout(resolve, 1000 * currentAttempt)); // Exponential backoff
            return attemptLoad();
          } else {
            // Max attempts reached, clear timeout and set failed state
            clearModuleTimeout(moduleName);
            attemptCounts.delete(moduleName);
            console.error(`Failed to load Justifi module ${moduleName} after ${MAX_RETRY_ATTEMPTS} attempts:`, error);

            sentry.error({
              error: `justifi module -> failed load final`,
              topic: 'loader-store-attempt-load',
              team: 'payments',
              addContext: {
                name: 'JustifiLoaderStore',
                context: {
                  moduleName,
                  error,
                  attempts: MAX_RETRY_ATTEMPTS,
                },
              },
            });

            set((state) => {
              state.components[componentName] = { isLoaded: true, isSuccess: false, isLoadingSlow: false };
            });
          }
        }
      };

      return attemptLoad();
    };

    const loadPaymentMethodModule = async (force = false) => {
      return loadJustifiModule('paymentMethod', importJustifiTokenizePaymentMethod, force);
    };

    const loadOnboardingModule = async (force = false) => {
      return loadJustifiModule('onboarding', importJustifiPaymentProvisioning, force);
    };

    const loadModules = async (modules: JustifiModules[], force = false) => {
      const promises = modules.map((moduleName) => {
        if (moduleName === 'onboarding') {
          return loadOnboardingModule(force);
        } else if (moduleName === 'paymentMethod') {
          return loadPaymentMethodModule(force);
        }
        console.warn(`Unknown Justifi moduleName: ${moduleName}`);
        return Promise.resolve();
      });
      await Promise.all(promises);
    };

    const resetStore = () => {
      // Clear any pending timeouts from module-level maps
      for (const [, timeoutId] of timeoutHandles) {
        clearTimeout(timeoutId);
      }
      timeoutHandles.clear();
      attemptCounts.clear();

      // Reset components to initial state
      set((state) => {
        state.components[COMPONENT_MODULE_MAP.onboarding] = { isLoaded: false, isSuccess: false, isLoadingSlow: false };
        state.components[COMPONENT_MODULE_MAP.paymentMethod] = {
          isLoaded: false,
          isSuccess: false,
          isLoadingSlow: false,
        };
      });
    };

    return {
      components: {
        [COMPONENT_MODULE_MAP.onboarding]: { isLoaded: false, isSuccess: false, isLoadingSlow: false },
        [COMPONENT_MODULE_MAP.paymentMethod]: { isLoaded: false, isSuccess: false, isLoadingSlow: false },
      } as LoaderStoreComponentsKeyed,
      isModuleLoaded,
      getModuleStatus,
      loadPaymentMethodModule,
      loadOnboardingModule,
      loadModules,
      resetStore,
    };
  },
  { name: 'JustifiLoader', trace: true }
);

export const useJustifiLoaderShallowStore = createShallowStore(useJustifiLoaderStore);
